// ------------------------------------------------------------------------------
// <copyright file="manualQaRunStorage.js" company="C2HL">
//     Copyright (c) 2024 C2HL. All rights reserved.
// </copyright>
// ------------------------------------------------------------------------------

/**
 * Local draft persistence for the Manual QA localhost runner.
 *
 * Sprint 1 intentionally stores tester progress locally so the AdminPortal UI can
 * be reviewed at localhost before the Functions/Cosmos API is added.
 */

export const MANUAL_QA_RUN_STORAGE_KEY = 'qolsync_manual_qa_run_state_v1';
export const MANUAL_QA_DEFAULT_CATALOG_ID = 'iosclient-view-plan-v1';

function createRunId(startedAt) {
  return `manual-qa-${startedAt}`;
}

function normalizeManualQaRunState(state) {
  const startedAt = state?.startedAt || Date.now();
  const runId = state?.runId || createRunId(startedAt);

  return {
    runId,
    assignmentId: state?.assignmentId || `assignment-${runId}`,
    catalogId: state?.catalogId || MANUAL_QA_DEFAULT_CATALOG_ID,
    startedAt,
    updatedAt: state?.updatedAt || startedAt,
    viewResults: state?.viewResults || {},
    completion: state?.completion || {
      testerReportedHours: 0,
      status: 'draft',
    },
  };
}

/**
 * Build a new empty Manual QA run state.
 *
 * @returns {object} Empty run state.
 */
export function createEmptyManualQaRunState() {
  const startedAt = Date.now();
  return normalizeManualQaRunState({
    startedAt,
    updatedAt: startedAt,
    viewResults: {},
  });
}

/**
 * Load the saved Manual QA run state from localStorage.
 *
 * @returns {object} Saved run state or a new empty state.
 */
export function loadManualQaRunState() {
  try {
    const raw = localStorage.getItem(MANUAL_QA_RUN_STORAGE_KEY);
    if (!raw) return createEmptyManualQaRunState();

    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !parsed.viewResults) {
      return createEmptyManualQaRunState();
    }

    return normalizeManualQaRunState(parsed);
  } catch {
    return createEmptyManualQaRunState();
  }
}

/**
 * Save a Manual QA run state to localStorage.
 *
 * @param {object} state - Run state to persist.
 * @returns {object} State with refreshed updatedAt timestamp.
 */
export function saveManualQaRunState(state) {
  const nextState = {
    ...normalizeManualQaRunState(state),
    updatedAt: Date.now(),
  };

  try {
    localStorage.setItem(MANUAL_QA_RUN_STORAGE_KEY, JSON.stringify(nextState));
  } catch {
    // Local draft persistence is best-effort until the backend API exists.
  }

  return nextState;
}

/**
 * Patch one view result inside a Manual QA run state.
 *
 * @param {object} state - Existing run state.
 * @param {string} viewId - View identifier from the catalog.
 * @param {object} patch - Partial view result.
 * @returns {object} New run state.
 */
export function updateManualQaViewResult(state, viewId, patch) {
  return {
    ...state,
    viewResults: {
      ...state.viewResults,
      [viewId]: {
        ...(state.viewResults[viewId] || {}),
        ...patch,
      },
    },
  };
}

/**
 * Remove all locally saved Manual QA progress.
 */
export function clearManualQaRunState() {
  try {
    localStorage.removeItem(MANUAL_QA_RUN_STORAGE_KEY);
  } catch {
    // Best-effort.
  }
}
